import { Injectable, OnDestroy } from '@angular/core';
import { Org } from '@app/models';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { LaunchDarklyService, LaunchDarklyUser } from './launch-darkly.service';

export enum FeatureFlagType {
    Dummy = 'Dummy' // if we need to use a Feature Flag in the future, add it to this enum, dummy value is for tests
}

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService implements OnDestroy {
    private client: LDClient;
    private readonly pendingOrgChanges: Org[] = [];
    private readonly loadedSource = new BehaviorSubject<boolean>(false);
    private readonly featureFlagObservables = new Map<FeatureFlagType, Observable<boolean>>();
    private readonly subscriptions = new Subscription();

    readonly loaded$ = this.loadedSource.asObservable();

    constructor(
        private readonly configService: ConfigService,
        private readonly launchDarklyService: LaunchDarklyService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService
    ) {}

    load(): void {
        // TODO: We can send user details to launch darkly if we want, to set flags for each user.
        // https://docs.launchdarkly.com/home/users/attributes
        const user = new LaunchDarklyUser('default-user');

        const settings = this.configService.getSettings();
        const clientId = settings.launchDarkly.clientId;

        this.client = this.launchDarklyService.getClient(clientId, user);
        this.client.on('ready', () => {
            this.loadedSource.next(true);

            this.subscriptions.add(
                this.orgSiteSelectionService.getOrgSelected$().subscribe(async org => {
                    this.pendingOrgChanges.push(org);
                    if (this.loadedSource.value === true) {
                        this.loadedSource.next(false);
                        while (this.pendingOrgChanges.length > 0) {
                            org = this.pendingOrgChanges.shift();
                            await this.client.close();
                            const user = this.getUser(org);
                            this.client = this.launchDarklyService.getClient(clientId, user);
                            await this.client.waitForInitialization();
                        }
                        this.loadedSource.next(true);
                    }
                })
            );
        });
    }

    getUser(userOrg: Org): LaunchDarklyUser {
        return new LaunchDarklyUser('default-user', userOrg.orgCode);
    }

    async ngOnDestroy(): Promise<void> {
        await this.client.close();
        this.subscriptions.unsubscribe();
    }

    getChangeListener(targetFeature: FeatureFlagType): BehaviorSubject<any> {
        const featureFlagListener = new BehaviorSubject(null);
        this.client.on('change:' + targetFeature, () => {
            featureFlagListener.next(null);
        });
        return featureFlagListener;
    }

    isFeatureEnabled(targetFeature: FeatureFlagType, defaultValue: boolean = false): Observable<boolean> {
        if (!this.featureFlagObservables.has(targetFeature)) {
            const changeListener = this.getChangeListener(targetFeature);
            this.featureFlagObservables.set(
                targetFeature,
                combineLatest([this.loaded$, changeListener]).pipe(
                    map(([isLoaded, _]) => (isLoaded ? this.client.variation(targetFeature as string, defaultValue) : defaultValue))
                )
            );
        }
        return this.featureFlagObservables.get(targetFeature);
    }

    /**
     * A synchronous way to determine whether the given Feature Flag is enabled.
     * This can erroneously return `false` if called while the application is initalising, as the Launch Darkly client may not yet be ready.
     */
    isFeatureEnabledSync(targetFeature: FeatureFlagType): boolean {
        return this.client.variation(targetFeature as string, false);
    }
}
